import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Text,
    FormControl,
    FormLabel,
    Input,
    SimpleGrid,
    Container,
    HStack,
    IconButton,
    Image as ChakraImage,
    useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useAuth } from '../Utils/AuthContext';
import { useAppToast } from '../Components/Toast';
import { createBusinessCardApi, getAllCardsApi, uploadPassportApi, normalizeCards } from '../Utils/ApiCall';
import { fetchStaffData } from '../Utils/SharePointService';
import { fetchAndLogEntraUserInfo, getCompanyWebsite, parseEntraPhones } from '../Utils/EntraService';
import { useMsal } from '@azure/msal-react';
import Navbar from '../Components/Navbar';
import { useCompany } from '../Utils/CompanyContext';

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dividerColor = useColorModeValue('gray.100', 'whiteAlpha.100');
    const labelColor = useColorModeValue('gray.500', 'gray.400');
    return (
        <HStack spacing={3} mb={5}>
            <Text
                fontSize="11px" fontWeight={700} color={labelColor}
                textTransform="uppercase" letterSpacing="0.1em"
                whiteSpace="nowrap"
            >
                {children}
            </Text>
            <Box flex={1} h="1px" bg={dividerColor} />
        </HStack>
    );
};

const CreateCard: React.FC = () => {
    const navigate = useNavigate();
    const toast = useAppToast();
    const { instance } = useMsal();
    const { user } = useAuth();
    const { company } = useCompany();
    const [loading, setLoading] = useState(false);
    const [fetchingStaff, setFetchingStaff] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const [formData, setFormData] = useState({
        name: '',
        role: '',
        company: company.companyName,
        department: '',
        unit: '',
        email: user?.email || '',
        phone: '',
        personalTel: '',
        website: company.website,
        photo: '',
    });

    const pageBg = useColorModeValue('#F4F6FB', '#070C18');
    const cardBg = useColorModeValue('#FFFFFF', '#0D1527');
    const cardBorder = useColorModeValue('rgba(13, 31, 76, 0.08)', 'rgba(255, 255, 255, 0.08)');
    const cardShadow = useColorModeValue(
        '0 20px 40px -15px rgba(13, 31, 76, 0.1)',
        '0 20px 40px -15px rgba(0, 0, 0, 0.6)'
    );

    const inputBg = useColorModeValue('#FFFFFF', 'rgba(255, 255, 255, 0.03)');
    const inputBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
    const inputColor = useColorModeValue('#0F172A', '#F8FAFC');
    const mutedInputBg = useColorModeValue('#F8FAFC', 'rgba(255, 255, 255, 0.05)');
    const mutedInputColor = useColorModeValue('gray.600', 'gray.400');
    const placeholderColor = useColorModeValue('gray.400', 'gray.500');
    const labelColor = useColorModeValue('gray.700', 'gray.300');
    const backBtnBg = useColorModeValue('white', 'whiteAlpha.100');
    const backBtnColor = useColorModeValue('#0F172A', 'white');
    const headingColor = useColorModeValue('#0B132B', '#F8FAFC');
    const sectionBorderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
    const photoRingBg = useColorModeValue('#EEF4FF', '#16233F');
    const photoRingBorder = useColorModeValue('white', '#0D1527');
    const photoPlaceholderColor = useColorModeValue('gray.500', 'gray.400');
    const uploadBorderColor = useColorModeValue('gray.300', 'whiteAlpha.300');
    const uploadHoverBg = useColorModeValue('brand.50', 'whiteAlpha.050');
    const uploadTitleColor = useColorModeValue('#0F172A', '#F8FAFC');
    const uploadDescColor = useColorModeValue('gray.500', 'gray.400');
    const footerBg = useColorModeValue('#F8FAFC', 'rgba(0, 0, 0, 0.2)');
    const footerTextColor = useColorModeValue('gray.500', 'gray.400');

    useEffect(() => {
        if (user) {
            getAllCardsApi().then((res) => {
                if (res.data.success) {
                    const cards = normalizeCards(res.data.data);
                    const myCard = cards.find(c => c.email === user.email);
                    if (myCard && myCard.status !== 'Rejected') {
                        navigate('/dashboard');
                    }
                }
            }).catch(() => {
                // Allow creation
            });

            // Fetch Entra ID & Staff Data to populate the card
            const loadUserData = async () => {
                try {
                    setFetchingStaff(true);

                    // 1. Fetch user profile from Microsoft Entra ID (Azure AD)
                    const entraUser = await fetchAndLogEntraUserInfo(instance);

                    if (entraUser) {
                        const { officialPhone, personalTel } = parseEntraPhones(
                            entraUser.businessPhones,
                            entraUser.mobilePhone
                        );
                        const companyName = entraUser.companyName || 'Eko Electricity Distribution Company';
                        const websiteUrl = getCompanyWebsite(companyName);

                        setFormData((prev) => ({
                            ...prev,
                            name: entraUser.displayName || prev.name,
                            role: entraUser.jobTitle || prev.role,
                            department: entraUser.department || prev.department,
                            email: entraUser.mail || entraUser.userPrincipalName || user.email || prev.email,
                            company: companyName,
                            website: websiteUrl,
                            phone: officialPhone || prev.phone,
                            personalTel: personalTel || prev.personalTel,
                        }));
                    }

                    // 2. Fetch SharePoint staff record for Function / Unit
                    try {
                        const tokenRes = await instance.acquireTokenSilent({
                            scopes: ["https://ekedp.sharepoint.com/AllSites.Read"],
                            account: (instance.getAllAccounts()[0]),
                        });

                        const staff = await fetchStaffData(user.email, tokenRes.accessToken);
                        if (staff) {
                            setFormData((prev) => ({
                                ...prev,
                                unit: staff.Function || staff.Current_Group || prev.unit,
                                name: prev.name || staff.StaffFullName || staff.Title,
                                role: prev.role || staff.Job_Role || staff.JobTitle1,
                                department: prev.department || staff.Department_x002d_Finance_x002d_O,
                                phone: prev.phone || staff.CUG,
                                personalTel: prev.personalTel || staff.Personal_Number,
                            }));
                        }
                    } catch (spError) {
                        // SharePoint lookup optional
                    }
                } catch (error) {
                    console.error("Failed to load user profile from Entra ID:", error);
                } finally {
                    setFetchingStaff(false);
                }
            };

            loadUserData();
        }
    }, [user, navigate, instance]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            if (file.size > 5 * 1024 * 1024) {
                toast({
                    title: 'File too large',
                    description: 'Image size should not exceed 5MB',
                    status: 'error',
                    duration: 3000,
                });
                return;
            }

            setSelectedFile(file);

            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData((prev) => ({
                    ...prev,
                    photo: reader.result as string,
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedFile) {
            toast({
                title: 'Photo required',
                description: 'Please upload a professional profile photo.',
                status: 'error',
                duration: 3000,
            });
            return;
        }

        if (!formData.name || !formData.role || !formData.email || !formData.phone) {
            toast({
                title: 'Missing information',
                description: 'Please fill in all required fields.',
                status: 'error',
                duration: 3000,
            });
            return;
        }

        setLoading(true);

        try {
            // Align payload with backend schema (POST /signup)
            const res = await createBusinessCardApi({
                fullName: formData.name,
                name: formData.name,
                email: formData.email,
                company: formData.company || company.companyName,
                department: formData.department,
                unit: formData.unit,
                jobRole: formData.role,
                phoneNumber: formData.phone,
                personalTel: formData.personalTel,
                website: formData.website || getCompanyWebsite(formData.company),
            });

            toast({
                title: 'Card submitted',
                description: 'Your business card has been sent for supervisor approval.',
                status: 'success',
                duration: 3000,
            });

            // Extract the generated ID from backend response (res.data.data.id)
            const resBody = res?.data as any;
            const createdCardId =
                resBody?.data?.id ||
                resBody?.data?._id ||
                resBody?.id ||
                resBody?._id;

            console.log('[CreateCard] /signup response:', resBody, 'Extracted Card ID for /upload/:id:', createdCardId);

            if (selectedFile) {
                const targetUploadId = createdCardId || user?.id;

                if (targetUploadId) {
                    try {
                        console.log(`[CreateCard] Uploading photo via POST /upload/${targetUploadId}...`);
                        await uploadPassportApi(targetUploadId, selectedFile);
                        toast({
                            title: 'Photo uploaded',
                            description: 'Profile photo uploaded successfully via /upload/:id.',
                            status: 'success',
                            duration: 3000,
                        });
                    } catch (uploadErr: any) {
                        console.error('[CreateCard] Photo upload error at /upload/:id:', uploadErr);
                        const status = uploadErr?.response?.status;
                        const errorMsg = uploadErr?.response?.data?.message || uploadErr?.message || 'Could not upload profile photo.';
                        toast({
                            title: `Image Upload Failed (/upload/:id${status ? ` - HTTP ${status}` : ''})`,
                            description: `${errorMsg} (Card details were saved, but photo upload to /upload/:id failed).`,
                            status: 'error',
                            duration: 6000,
                        });
                    }
                } else {
                    console.warn('[CreateCard] No card ID returned from /signup to execute /upload/:id');
                    toast({
                        title: 'Image Upload Skipped',
                        description: 'Server did not return a card ID to upload image to /upload/:id.',
                        status: 'warning',
                        duration: 5000,
                    });
                }
            }

            navigate('/dashboard');
        } catch (error: any) {
            console.error('[CreateCard] Card submission error at /signup:', error);
            const status = error?.response?.status;
            const errorMsg = error?.response?.data?.message || error?.message || 'Please try again.';
            toast({
                title: `Card Submission Failed (/signup${status ? ` - HTTP ${status}` : ''})`,
                description: status === 413
                    ? 'Request Entity Too Large (413). Please ensure profile photo is not included in the signup data.'
                    : errorMsg,
                status: 'error',
                duration: 5000,
            });
        } finally {
            setLoading(false);
        }
    };

    const inputStyles = {
        h: '44px',
        border: '1px solid',
        borderColor: inputBorder,
        borderRadius: '10px',
        fontSize: '13.5px',
        bg: inputBg,
        color: inputColor,
        _placeholder: { color: placeholderColor },
        _focus: {
            borderColor: 'brand.500',
            boxShadow: `0 0 0 3px rgba(18, 100, 232, 0.15)`,
        },
    };

    const mutedInputStyles = {
        ...inputStyles,
        bg: mutedInputBg,
        color: mutedInputColor,
        cursor: 'default',
    };

    const labelStyles = {
        fontSize: '12px',
        fontWeight: 600,
        color: labelColor,
        mb: 1.5,
    };

    return (
        <Box bg={pageBg} minH="100vh" fontFamily="'Inter', sans-serif" transition="background 0.25s ease">
            {/* Navbar */}
            <Navbar />

            {/* Content */}
            <Container maxW="720px" py={{ base: 6, md: 10 }} pb={16}>
                {/* Page topbar */}
                <HStack spacing={4} mb={8}>
                    <IconButton
                        aria-label="Back"
                        icon={<ArrowBackIcon />}
                        size="sm"
                        bg={backBtnBg}
                        border="1px solid"
                        borderColor={cardBorder}
                        borderRadius="full"
                        color={backBtnColor}
                        _hover={{ transform: 'translateX(-2px)' }}
                        onClick={() => navigate('/dashboard')}
                    />
                    <Text
                        fontFamily="'Cormorant Garamond', Georgia, serif"
                        fontSize={{ base: "30px", md: "38px" }}
                        fontWeight={400}
                        color={headingColor}
                        letterSpacing="-0.3px"
                    >
                        Create Digital Card
                    </Text>
                </HStack>

                {/* Form card */}
                <Box
                    as="form"
                    onSubmit={handleSubmit}
                    bg={cardBg}
                    border="1px solid"
                    borderColor={cardBorder}
                    borderRadius="20px"
                    boxShadow={cardShadow}
                    overflow="hidden"
                >
                    {/* Top Multi-Color Brand Stripe */}
                    <Box
                        h="3px"
                        w="full"
                        bg="linear-gradient(90deg, #00B4D8 0%, #1565C0 35%, #FFB703 70%, #F35B04 100%)"
                    />

                    {/* Section: Personal Information */}
                    <Box px={{ base: 6, md: 8 }} pt={7} pb={7} borderBottom="1px solid" borderColor={sectionBorderColor}>
                        <SectionLabel>Personal Information</SectionLabel>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                            <FormControl isRequired>
                                <FormLabel sx={labelStyles}>Full Name</FormLabel>
                                <Input
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Taiwo Emmanuel Ikusemoran"
                                    isReadOnly={!!formData.name && !fetchingStaff}
                                    sx={formData.name && !fetchingStaff ? mutedInputStyles : inputStyles}
                                />
                            </FormControl>
 
                            <FormControl isRequired>
                                <FormLabel sx={labelStyles}>Job Role / Title</FormLabel>
                                <Input
                                    name="role"
                                    value={formData.role}
                                    onChange={handleInputChange}
                                    placeholder="General Manager"
                                    isReadOnly={!!formData.role && !fetchingStaff}
                                    sx={formData.role && !fetchingStaff ? mutedInputStyles : inputStyles}
                                />
                            </FormControl>
 
                            <FormControl isRequired>
                                <FormLabel sx={labelStyles}>Department</FormLabel>
                                <Input
                                    name="department"
                                    value={formData.department}
                                    onChange={handleInputChange}
                                    placeholder="Sales / Marketing"
                                    isReadOnly={!!formData.department && !fetchingStaff}
                                    sx={formData.department && !fetchingStaff ? mutedInputStyles : inputStyles}
                                />
                            </FormControl>
 
                            <FormControl isRequired>
                                <FormLabel sx={labelStyles}>Unit</FormLabel>
                                <Input
                                    name="unit"
                                    value={formData.unit}
                                    onChange={handleInputChange}
                                    placeholder="Technical Unit"
                                    isReadOnly={!!formData.unit && !fetchingStaff}
                                    sx={formData.unit && !fetchingStaff ? mutedInputStyles : inputStyles}
                                />
                            </FormControl>
                        </SimpleGrid>
                    </Box>

                    {/* Section: Contact Details */}
                    <Box px={{ base: 6, md: 8 }} pt={7} pb={7} borderBottom="1px solid" borderColor={sectionBorderColor}>
                        <SectionLabel>Contact Details</SectionLabel>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                            <FormControl isRequired>
                                <FormLabel sx={labelStyles}>Email Address</FormLabel>
                                <Input
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="t.ikusemoran@ekopowerlimited.com"
                                    isReadOnly={!!formData.email && !fetchingStaff}
                                    sx={formData.email && !fetchingStaff ? mutedInputStyles : inputStyles}
                                />
                            </FormControl>
 
                            <FormControl isRequired>
                                <FormLabel sx={labelStyles}>Official Phone Number</FormLabel>
                                <Input
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="0703 690 3640"
                                    sx={inputStyles}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel sx={labelStyles}>Personal Tel (Optional)</FormLabel>
                                <Input
                                    name="personalTel"
                                    value={formData.personalTel}
                                    onChange={handleInputChange}
                                    placeholder="0802 345 6789"
                                    sx={inputStyles}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel sx={labelStyles}>Company</FormLabel>
                                <Input
                                    name="company"
                                    value={formData.company}
                                    onChange={handleInputChange}
                                    isReadOnly
                                    sx={mutedInputStyles}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel sx={labelStyles}>Official Website</FormLabel>
                                <Input
                                    name="website"
                                    value={formData.website}
                                    onChange={handleInputChange}
                                    isReadOnly
                                    sx={mutedInputStyles}
                                />
                            </FormControl>
                        </SimpleGrid>
                    </Box>

                    {/* Section: Profile Photo */}
                    <Box px={{ base: 6, md: 8 }} pt={7} pb={7} borderBottom="1px solid" borderColor={sectionBorderColor}>
                        <SectionLabel>Profile Photo</SectionLabel>
                        <HStack spacing={5} align="center">
                            {/* Avatar preview with halo ring */}
                            <Box
                                boxSize="84px"
                                borderRadius="full"
                                p="2px"
                                bg="linear-gradient(135deg, #00B4D8, #FFB703, #F35B04)"
                                flexShrink={0}
                            >
                                <Box
                                    boxSize="full"
                                    borderRadius="full"
                                    bg={photoRingBg}
                                    overflow="hidden"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    border="2px solid"
                                    borderColor={photoRingBorder}
                                >
                                    {formData.photo ? (
                                        <ChakraImage src={formData.photo} alt="Preview" boxSize="full" objectFit="cover" />
                                    ) : (
                                        <Text fontSize="11px" color={photoPlaceholderColor} textAlign="center" fontWeight={600}>
                                            No photo
                                        </Text>
                                    )}
                                </Box>
                            </Box>

                            {/* Upload zone */}
                            <Box
                                flex={1}
                                border="1.5px dashed"
                                borderColor={uploadBorderColor}
                                borderRadius="14px"
                                px={5}
                                py={4}
                                cursor="pointer"
                                transition="all 0.2s"
                                _hover={{
                                    borderColor: 'brand.500',
                                    bg: uploadHoverBg,
                                }}
                                position="relative"
                            >
                                <Text fontSize="13px" fontWeight={600} color={uploadTitleColor} mb={0.5}>
                                    Upload a profile photo
                                </Text>
                                <Text fontSize="11px" color={uploadDescColor}>
                                    JPG or PNG · Max 5 MB · Professional executive headshot recommended
                                </Text>
                                <Input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    position="absolute"
                                    inset={0}
                                    opacity={0}
                                    cursor="pointer"
                                    h="full"
                                    w="full"
                                />
                            </Box>
                        </HStack>
                    </Box>

                    {/* Footer */}
                    <Box px={{ base: 6, md: 8 }} py={6} bg={footerBg}>
                        <Button
                            type="submit"
                            w="full"
                            h="50px"
                            bg="linear-gradient(135deg, #0B2C6D 0%, #1255C8 100%)"
                            color="white"
                            borderRadius="12px"
                            fontSize="14px"
                            fontWeight={600}
                            letterSpacing="0.02em"
                            isLoading={loading}
                            isDisabled={!selectedFile}
                            loadingText="Submitting…"
                            boxShadow="0 4px 16px rgba(18, 85, 200, 0.25)"
                            _hover={{
                                bg: 'linear-gradient(135deg, #0E398C 0%, #1664EC 100%)',
                                transform: 'translateY(-1px)',
                            }}
                            _active={{ transform: 'translateY(0)' }}
                            _disabled={{
                                opacity: 0.5,
                                cursor: 'not-allowed',
                                transform: 'none',
                                boxShadow: 'none',
                            }}
                            transition="all 0.2s"
                        >
                            Submit for Supervisor Approval
                        </Button>
                        {!selectedFile && (
                            <Text fontSize="11px" color="orange.400" textAlign="center" mt={2} fontWeight={500}>
                                Please upload a profile photo to enable card submission.
                            </Text>
                        )}
                        <Text fontSize="11.5px" color={footerTextColor} textAlign="center" mt={3}>
                            Your card will be verified by a supervisor before going live on the network.
                        </Text>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default CreateCard;